import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

const NODE_W = 120;

const SECTION_THEMES = {
  intro: { palette: "warning", emoji: "✦" },
  collar: { palette: "secondary", emoji: "◎" },
  messages: { palette: "primary", emoji: "◆" },
  solutions: { palette: "info", emoji: "◇" },
  conclusion: { palette: "success", emoji: "★" },
};

const getSectionColor = (theme, paletteKey) =>
  theme.palette[paletteKey]?.main ?? theme.palette.primary.main;

const Spine = ({ color, flex = 1, minHeight = 5 }) => (
  <Box
    sx={{
      width: 3,
      flex,
      minHeight,
      borderRadius: 2,
      background: `linear-gradient(180deg, ${alpha(color, 0.15)}, ${color}, ${alpha(color, 0.15)})`,
      mx: "auto",
      opacity: 0.85,
    }}
  />
);

const StructureBox = ({ label, color, tall = false, showSpineBelow = false }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: NODE_W,
      flexShrink: 0,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        px: 1,
        py: tall ? 1.2 : 0.7,
        width: "100%",
        borderRadius: 2,
        textAlign: "center",
        border: "1.5px solid",
        borderColor: alpha(color, 0.45),
        bgcolor: alpha(color, 0.09),
        backgroundImage: `linear-gradient(145deg, ${alpha(color, 0.14)} 0%, ${alpha(color, 0.04)} 100%)`,
        boxShadow: `0 4px 14px ${alpha(color, 0.12)}`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: color,
          borderRadius: "3px 0 0 3px",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          lineHeight: 1.28,
          display: "block",
          color: color,
          fontSize: "0.71rem",
          letterSpacing: -0.1,
        }}
      >
        {label}
      </Typography>
    </Paper>
    {showSpineBelow ? <Spine color={color} /> : null}
  </Box>
);

const HintCard = ({ children, color, compact = false }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: compact ? 0.85 : 1,
      borderRadius: 2,
      border: "1px solid",
      borderColor: alpha(color, 0.22),
      bgcolor: alpha(color, 0.05),
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      justifyContent: "center",
    }}
  >
    {children}
  </Box>
);

const ConnectorLine = ({ color, multi }) => (
  <Box
    sx={{
      width: 20,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      alignSelf: multi ? "center" : "flex-start",
      mt: multi ? 0 : 1.25,
    }}
  >
    <Box
      sx={{
        width: "100%",
        height: 2,
        borderRadius: 1,
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.25)})`,
      }}
    />
  </Box>
);

const ConnectorBracket = ({ color, align = "flex-start" }) => (
  <Box
    sx={{
      width: 16,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      alignSelf: align === "center" ? "center" : "stretch",
    }}
  >
    <Box
      sx={{
        width: "100%",
        alignSelf: "stretch",
        my: align === "center" ? 0 : 0.35,
        borderTop: `2px solid ${alpha(color, 0.55)}`,
        borderBottom: `2px solid ${alpha(color, 0.55)}`,
        borderRight: `2px solid ${alpha(color, 0.55)}`,
        borderRadius: "0 6px 6px 0",
        boxShadow: `2px 0 8px ${alpha(color, 0.08)}`,
      }}
    />
  </Box>
);

const LineHints = ({ color, children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  const multi = items.length > 1;

  return (
    <Box sx={{ display: "flex", alignItems: multi ? "center" : "flex-start", flex: 1, minWidth: 0 }}>
      <ConnectorLine color={color} multi={multi} />
      <HintCard color={color} compact={!multi}>
        {items}
      </HintCard>
    </Box>
  );
};

const BracketHints = ({ color, align = "flex-start", children }) => (
  <Box sx={{ display: "flex", alignItems: "stretch", flex: 1, minWidth: 0 }}>
    <ConnectorBracket color={color} align={align} />
    <HintCard color={color}>{children}</HintCard>
  </Box>
);

const HintLine = ({ children, orPrefix }) => (
  <Typography
    variant="caption"
    sx={{
      lineHeight: 1.5,
      color: "text.primary",
      fontWeight: 600,
      fontSize: "0.72rem",
      display: "flex",
      alignItems: "flex-start",
      gap: 0.5,
    }}
  >
    {orPrefix ? (
      <Box
        component="span"
        sx={{
          fontWeight: 800,
          color: "text.secondary",
          fontSize: "0.65rem",
          mt: 0.15,
          flexShrink: 0,
        }}
      >
        {orPrefix}
      </Box>
    ) : null}
    <Box component="span">{children}</Box>
  </Typography>
);

const GuideRow = ({ color, children, nodeProps, groupedNodes }) => (
    <Box
      sx={{
        display: "flex",
        gap: 0.75,
        mb: groupedNodes ? 0.25 : 0.35,
        alignItems: "stretch",
        p: 0.75,
        mx: -0.75,
        borderRadius: 2.5,
        transition: "background 0.2s ease",
        "&:hover": {
          bgcolor: alpha(color, 0.04),
        },
      }}
    >
      {groupedNodes ? (
        <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
          {groupedNodes.map((node, index) => (
            <StructureBox
              key={node.label}
              label={node.label}
              color={color}
              showSpineBelow={index < groupedNodes.length - 1 || nodeProps?.showSpineBelow}
            />
          ))}
        </Box>
      ) : (
        <StructureBox label={nodeProps.label} color={color} tall={nodeProps.tall} showSpineBelow={nodeProps.showSpineBelow} />
      )}
      {children}
    </Box>
);

const EstructuraGuiaPanel = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const orLabel = t("crearMensajes.introOr").toLowerCase();

  const color = (key) => getSectionColor(theme, SECTION_THEMES[key].palette);

  const collarHint = t("crearMensajes.structureGuide.collar.hint");
  const [collarTitle, ...collarRest] = collarHint.includes(":")
    ? [
        collarHint.slice(0, collarHint.indexOf(":") + 1),
        collarHint.slice(collarHint.indexOf(":") + 1).trim(),
      ]
    : [collarHint, null];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.8),
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 45%)`
            : `linear-gradient(160deg, ${alpha("#eef4ff", 0.85)} 0%, ${alpha(theme.palette.background.paper, 0.99)} 50%, ${alpha("#f4faf6", 0.45)} 100%)`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 28px rgba(0,0,0,0.22)"
            : "0 10px 36px rgba(15,23,42,0.06)",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.6),
          background: alpha(theme.palette.background.paper, 0.55),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            }}
          >
            <AccountTreeOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {t("crearMensajes.structureGuide.title")}
            </Typography>
            <Chip
              size="small"
              label="7 pasos · guía visual"
              sx={{
                mt: 0.4,
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 1.25 }}>
        <GuideRow
          color={color("intro")}
          nodeProps={{
            label: t("crearMensajes.structureGuide.intro.label"),
            tall: true,
            showSpineBelow: true,
          }}
        >
          <BracketHints color={color("intro")}>
            <HintLine>{t("crearMensajes.structureGuide.intro.hint1")}</HintLine>
            <HintLine orPrefix={orLabel}>
              {t("crearMensajes.structureGuide.intro.hint2")}:
            </HintLine>
            <HintLine orPrefix={orLabel}>
              {t("crearMensajes.structureGuide.intro.hint3")}
            </HintLine>
          </BracketHints>
        </GuideRow>

        <GuideRow
          color={color("collar")}
          nodeProps={{
            label: t("crearMensajes.structureGuide.collar.label"),
            showSpineBelow: true,
          }}
        >
          <LineHints color={color("collar")}>
            <HintLine>{collarTitle}</HintLine>
            {collarRest[0] ? <HintLine>{collarRest[0]}</HintLine> : null}
          </LineHints>
        </GuideRow>

        <GuideRow
          color={color("messages")}
          groupedNodes={[
            { label: t("crearMensajes.structureGuide.messages.one") },
            { label: t("crearMensajes.structureGuide.messages.two") },
            { label: t("crearMensajes.structureGuide.messages.three") },
          ]}
          nodeProps={{ showSpineBelow: true }}
        >
          <BracketHints color={color("messages")} align="center">
            <HintLine>{t("crearMensajes.structureGuide.messages.hint")}</HintLine>
          </BracketHints>
        </GuideRow>

        <GuideRow
          color={color("solutions")}
          nodeProps={{
            label: t("crearMensajes.structureGuide.solutions.label"),
            showSpineBelow: true,
          }}
        >
          <LineHints color={color("solutions")}>
            <HintLine>{t("crearMensajes.structureGuide.solutions.hint1")}</HintLine>
            <HintLine>{t("crearMensajes.structureGuide.solutions.hint2")}</HintLine>
          </LineHints>
        </GuideRow>

        <GuideRow
          color={color("conclusion")}
          nodeProps={{
            label: t("crearMensajes.structureGuide.conclusion.label"),
            tall: true,
          }}
        >
          <BracketHints color={color("conclusion")}>
            <HintLine>{t("crearMensajes.structureGuide.conclusion.hint1")}</HintLine>
            <HintLine>{t("crearMensajes.structureGuide.conclusion.hint2")}</HintLine>
            <HintLine>{t("crearMensajes.structureGuide.conclusion.hint3")}</HintLine>
          </BracketHints>
        </GuideRow>
      </Box>
    </Paper>
  );
};

export default EstructuraGuiaPanel;
